import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Plus, X, Loader2 } from 'lucide-react';

import { budgetService } from '../services/budgetService';
import { purchaseService } from '../services/purchaseService';
import { useAppContext } from '../context/AppContext';
import MantleGrid, { MantleGridButton } from './MantleGrid';
import { cell, GRID } from '../styles/gridStyles';
import { periodLabel, PERIODS } from '../utils/financialPeriods';

const stableKey = (it) => it.stock_code || it.item_label;

// The procurement-owned price_source states (vs MANUAL).
const PROC_SOURCES = ['PROCUREMENT', 'PROC_NOT_SET', 'PROC_UOM'];
const fmtMoney = (n) =>
  (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ---- Add-item modal ----------------------------------------------------------
// Type to search existing non-stocked items (this version+site) or enter a new
// label, with an optional toggle to also register it as a bud_opex_item.
const AddOpexItemModal = ({ existingKeys, catalog, canRegister, busy, error, onAdd, onClose }) => {
  const [query, setQuery] = useState('');
  const [uom, setUom] = useState('');
  const [alsoRegister, setAlsoRegister] = useState(true);

  const label = query.trim();
  const wantsRegister = alsoRegister && canRegister; // procurement item
  const dup = label && existingKeys.has(label);
  const uomMissing = wantsRegister && !uom.trim(); // UoM is required for procurement items
  const problem = dup
    ? 'An item with that description already exists on this line.'
    : uomMissing
      ? 'Unit of measure is required for procurement items.'
      : '';
  const canAdd = !!label && !dup && !uomMissing && !busy;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95 flex items-start justify-between gap-3">
          <div>
            <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">Add item</h3>
            <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Search an existing non-stocked item or type a new one.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5">
          <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            Item description
            <input
              type="text"
              list="opex-item-catalog"
              className="text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400"
              placeholder="Type to search or enter a new item…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            <datalist id="opex-item-catalog">
              {catalog.map((l) => <option key={l} value={l} />)}
            </datalist>
          </label>

          <label className="mt-4 flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            Unit of measure {wantsRegister ? <span className="text-red-600 dark:text-red-400">(required)</span> : '(optional)'}
            <input
              type="text"
              className={`text-sm rounded-md border bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400 ${
                uomMissing ? 'border-red-400 dark:border-red-500' : 'border-slate-300 dark:border-slate-600'
              }`}
              placeholder="e.g. each, kg, hrs"
              value={uom}
              onChange={(e) => setUom(e.target.value)}
            />
          </label>

          <label className={`mt-4 flex items-start gap-2.5 text-sm ${canRegister ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400 dark:text-slate-500'}`}>
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-slate-600 accent-emerald-600"
              checked={alsoRegister && canRegister}
              disabled={!canRegister || busy}
              onChange={(e) => setAlsoRegister(e.target.checked)}
            />
            <span>
              Also add to Non-Stocked items
              <span className="block text-xs text-slate-400">
                {canRegister
                  ? "Registers the item against this line's site & version so it can be priced by procurement"
                  : 'Select a budget version in the top bar to enable this.'}
              </span>
            </span>
          </label>

          <p className="m-0 mt-2 min-h-[1rem] text-xs text-red-600 dark:text-red-400">{problem || error || ''}</p>

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onAdd(label, alsoRegister && canRegister, uom)}
              disabled={!canAdd}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white transition-colors ${
                canAdd ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600'
              }`}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {busy ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// OpexBuildGrid
// Editor for ONE GL line's P*Q detail. Each item renders either:
//   - expanded: three stacked rows  Qty / Price / Value, OR
//   - collapsed: a single summary row showing the per-period Value
// across the 12 month columns.
//
// A sticky "GL Value" row at the top shows the per-period totals plus the
// annual grand total (what posts to the GL line). Users can add new manual
// items via the toolbar.
//
// Props:
//   glLineId   the bud_gl_lines.gl_line_id to edit
//   lineMeta   optional { account_code, account_description, cost_center_code }
//   onClose    called when the user closes the popup
//   onSaved    optional — called after a successful save so the parent can
//              refresh the budget grid (GL values changed via the trigger)
// =============================================================================
const OpexBuildGrid = ({ glLineId, lineMeta, onClose, onSaved }) => {
  const { fyStartMonth, selectedVersion } = useAppContext();
  const [items, setItems] = useState([]);      // [{ item_label, stock_code, periods{}, proc_price{} }]
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [dirty, setDirty] = useState({});      // { `${key}:${period}`: true }
  const [collapsed, setCollapsed] = useState({}); // { [key]: true }

  // Add-item modal state
  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState('');
  const [catalog, setCatalog] = useState([]);    // existing non-stocked labels (search)
  const catalogLoaded = useRef(false);

  // ---- Load build detail --------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const res = await budgetService.getOpexBuild(glLineId);
        if (cancelled) return;
        const data = res?.data || res;
        const loaded = data?.items || [];
        setItems(loaded);
        // Open collapsed: one summary line per item, expand on demand.
        const allCollapsed = {};
        loaded.forEach((it) => { allCollapsed[stableKey(it)] = true; });
        setCollapsed(allCollapsed);
      } catch (err) {
        console.error('Failed to load build:', err.response?.data || err.message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [glLineId]);

  // Is a given item/period price procurement-driven (=> price cell read-only)?
  // This covers catalogue prices resolved from proc_item_prices (get_proc_price).
  const isProcPrice = useCallback((item, period) => {
    return item.proc_price?.[period] != null;
  }, []);

  // Is the whole item procurement-priced? True when it was registered as a
  // non-stocked item (bud_opex_items) on add, or when any of its saved periods
  // carries a procurement source — PROCUREMENT (priced) or PROC_NOT_SET
  // (pending). Survives a reload via the persisted price_source.
  const procurementItem = useCallback((item) => (
    item.procurement === true ||
    Object.values(item.periods || {}).some((c) => PROC_SOURCES.includes(c?.price_source))
  ), []);

  // Does the item still have a price procurement hasn't set? Drives the ❗ flag.
  const priceNotSet = useCallback((item) => (
    Object.values(item.periods || {}).some((c) => c?.price_source === 'PROC_NOT_SET')
  ), []);

  // Did procurement change the UoM after pricing? The item needs its quantities
  // re-checked; editing any quantity clears it. Drives the ⚠️ flag.
  const uomChanged = useCallback((item) => (
    Object.values(item.periods || {}).some((c) => c?.price_source === 'PROC_UOM')
  ), []);

  const cellOf = (item, period) =>
    item.periods?.[period] || { quantity: 0, unit_price: 0, price_source: 'MANUAL', price_id: null };

  // Resolved value (qty * effective price) for one item/period.
  const periodValue = useCallback((item, period) => {
    const c = cellOf(item, period);
    const price = isProcPrice(item, period) ? item.proc_price[period] : c.unit_price;
    return (c.quantity || 0) * (price || 0);
  }, [isProcPrice]);

  // ---- Totals (per period + annual grand total) ---------------------------
  const periodTotals = useMemo(() => {
    const totals = {};
    for (const p of PERIODS) {
      totals[p] = items.reduce((acc, item) => acc + periodValue(item, p), 0);
    }
    return totals;
  }, [items, periodValue]);

  const grandTotal = useMemo(
    () => PERIODS.reduce((acc, p) => acc + (periodTotals[p] || 0), 0),
    [periodTotals]
  );

  // ---- Build columns: label col + 12 month cols ---------------------------
  const columns = useMemo(() => ([
    { columnId: 'rowlabel', width: 230, resizable: true },
    { columnId: 'uom', width: 64, resizable: true },
    ...PERIODS.map((p) => ({ columnId: `P${p}`, width: 84, resizable: true })),
  ]), []);

  // ---- Build rows ---------------------------------------------------------
  const gridRows = useMemo(() => {
    const headerRow = {
      rowId: 'header',
      height: GRID.size.headerRow,
      cells: [
        cell.header('Item / Measure'),
        cell.header('UoM'),
        ...PERIODS.map((p) => cell.header(periodLabel(p, fyStartMonth), { align: 'right' })),
      ],
    };

    // Sticky GL-value row: per-period totals + annual grand total in the label.
    const glValueRow = {
      rowId: 'glvalue',
      height: GRID.size.totalRow,
      cells: [
        cell.totalLabel(`GL Value · ${fmtMoney(grandTotal)}`),
        cell.totalBlank(),
        ...PERIODS.map((p) => cell.totalNumber(periodTotals[p])),
      ],
    };

    const rows = [headerRow, glValueRow];

    items.forEach((item, idx) => {
      const key = stableKey(item);

      // ❗ = procurement price still pending (PROC_NOT_SET); ⚠️ = procurement
      // changed the UoM, quantities need re-checking (PROC_UOM).
      const flag = priceNotSet(item) ? '❗ ' : uomChanged(item) ? '⚠️ ' : '';

      if (collapsed[key]) {
        // Collapsed: single summary row showing each period's Value.
        rows.push({
          rowId: `${key}::summary`,
          height: GRID.size.dataRow,
          cells: [
            cell.label(`${flag}▸ ${item.item_label}`, { idx }),
            cell.editText(item.unit_of_measure, { idx }),
            ...PERIODS.map((p) => cell.number(periodValue(item, p), { editable: false, idx })),
          ],
        });
        return;
      }

      // Expanded: Qty / Price / Value. The Qty label toggles collapse.
      rows.push({
        rowId: `${key}::qty`,
        height: GRID.size.dataRow,
        cells: [
          cell.label(`${flag}▾ ${item.item_label}  ·  Qty`, { idx }),
          cell.editText(item.unit_of_measure, { idx }),
          ...PERIODS.map((p) => cell.number(cellOf(item, p).quantity, { editable: true, idx })),
        ],
      });

      const procItem = procurementItem(item);
      rows.push({
        rowId: `${key}::price`,
        height: GRID.size.dataRow,
        cells: [
          cell.label('Price', { idx, bold: false, align: 'right' }),
          cell.text('', { idx }),
          ...PERIODS.map((p) => {
            const proc = isProcPrice(item, p);
            const value = proc ? item.proc_price[p] : cellOf(item, p).unit_price;
            // Procurement-priced cells are read-only (set by procurement, not here).
            return cell.number(value, { editable: !proc && !procItem, idx });
          }),
        ],
      });

      rows.push({
        rowId: `${key}::value`,
        height: GRID.size.dataRow,
        cells: [
          cell.label('Value', { idx, bold: false, align: 'right' }),
          cell.text('', { idx }),
          ...PERIODS.map((p) => cell.number(periodValue(item, p), { editable: false, idx })),
        ],
      });
    });

    // Per-period totals across all items (sticky bottom).
    rows.push({
      rowId: 'totals',
      height: GRID.size.totalRow,
      cells: [
        cell.totalLabel('TOTAL'),
        cell.totalBlank(),
        ...PERIODS.map((p) => cell.totalNumber(periodTotals[p])),
      ],
    });

    return rows;
  }, [items, collapsed, isProcPrice, procurementItem, priceNotSet, uomChanged, periodValue, periodTotals, grandTotal, fyStartMonth]);

  // ---- Collapse / expand --------------------------------------------------
  // Clicking an item's label (Qty row when expanded, summary row when
  // collapsed) toggles that item between the two layouts.
  const handleFocusLocationChanging = (location) => {
    const colId = String(location.columnId);
    const rowId = String(location.rowId);
    if (colId === 'rowlabel' && (rowId.endsWith('::qty') || rowId.endsWith('::summary'))) {
      const key = rowId.split('::')[0];
      setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
      return false;
    }
    return true;
  };

  const collapseAll = () => {
    const all = {};
    items.forEach((it) => { all[stableKey(it)] = true; });
    setCollapsed(all);
  };
  const expandAll = () => setCollapsed({});

  // ---- Add a new item (via modal) ----------------------------------------
  // Only a version is required. The item is registered against the GL line's
  // OWN site (the API resolves it from gl_line_id), so "All sites" in the top
  // bar is fine — it no longer has to be narrowed to one site first.
  const canRegister = !!selectedVersion;
  const existingKeys = useMemo(() => new Set(items.map((it) => stableKey(it))), [items]);

  // Open the modal and lazily load the non-stocked item catalogue for search.
  const openAdd = async () => {
    setAddError('');
    setAddOpen(true);
    if (!catalogLoaded.current && canRegister) {
      try {
        const r = await purchaseService.getNonStockedItems();
        if (r?.success) { setCatalog(r.data.items || []); catalogLoaded.current = true; }
      } catch {
        /* search suggestions are optional — manual entry still works */
      }
    }
  };

  // Manual items have no stock_code, so the item_label is the key. The DB unique
  // key is (gl_line_id, item_key, period), so descriptions must be unique within
  // this GL line. If requested, also register the item in bud_opex_items.
  const confirmAdd = async (label, alsoRegister, uom) => {
    if (items.some((it) => stableKey(it) === label)) {
      setAddError('An item with that description already exists on this line.');
      return;
    }

    // When registered, the item is priced by procurement (bud_opex_items). Seed
    // each period from the returned cost: a set price (> 0) counts as already
    // PROCUREMENT-priced; anything else is PROC_NOT_SET (pending — shows a ❗).
    const periods = {};
    if (alsoRegister) {
      setAddBusy(true);
      setAddError('');
      try {
        const r = await purchaseService.registerNonStockedItem(label, (uom || '').trim() || null, glLineId);
        if (!r?.success) throw new Error(r?.message || 'Could not add to Non-Stocked items.');
        const costs = r.data?.costs || {};
        for (let p = 1; p <= 12; p++) {
          const cost = Number(costs[p] || 0);
          periods[p] = {
            quantity: 0,
            unit_price: cost,
            price_source: cost > 0 ? 'PROCUREMENT' : 'PROC_NOT_SET',
            price_id: null,
          };
        }
        if (!catalog.includes(label)) setCatalog((prev) => [...prev, label].sort());
      } catch (e) {
        setAddError(e.response?.data?.message || e.message || 'Could not add to Non-Stocked items.');
        setAddBusy(false);
        return; // keep the modal open; nothing was added
      }
      setAddBusy(false);
    }

    const proc_price = {};
    for (let p = 1; p <= 12; p++) proc_price[p] = null;

    setItems((prev) => [...prev, {
      item_label: label,
      stock_code: null,
      unit_of_measure: (uom || '').trim() || null,
      periods,
      proc_price,
      procurement: alsoRegister,
    }]);
    setCollapsed((prev) => { const next = { ...prev }; delete next[label]; return next; });
    setAddOpen(false);
    setAddError('');
  };

  // ---- Edits --------------------------------------------------------------
  const handleChanges = (changes) => {
    setItems((prev) => {
      const next = prev.map((it) => ({
        ...it,
        periods: { ...it.periods },
        proc_price: it.proc_price,
      }));
      // A multi-cell paste arrives as one onCellsChanged call PER CELL in a
      // single tick. Collect this call's keys locally and merge them with a
      // functional setDirty — seeding from the `dirty` closure instead would
      // restart from the same stale base each call and only the last pasted
      // cell would survive to be saved.
      const touched = {};

      for (const ch of changes) {
        const rowId = String(ch.rowId);
        const colId = String(ch.columnId);
        const [key, kind] = rowId.split('::');

        // UoM: a free-text, item-level attribute edited on the item's main row
        // (summary when collapsed, qty when expanded). Null/blank clears it.
        if (colId === 'uom') {
          if (kind !== 'qty' && kind !== 'summary') continue;
          const item = next.find((it) => stableKey(it) === key);
          if (!item) continue;
          item.unit_of_measure = (ch.newCell?.text ?? '').trim() || null;
          // Re-save the UoM onto the item's existing DB rows; new periods pick it
          // up when their qty is entered (every change carries the UoM).
          for (const p of Object.keys(item.periods)) {
            if (item.periods[p]?.opex_line_id) touched[`${key}:${p}`] = true;
          }
          continue;
        }

        if (!colId.startsWith('P')) continue;
        if (kind !== 'qty' && kind !== 'price') continue; // value/summary are read-only

        const period = Number(colId.slice(1));
        const num = Number(ch.newCell?.value);
        if (isNaN(num)) continue;

        const item = next.find((it) => stableKey(it) === key);
        if (!item) continue;

        // Block edits to procurement-driven price cells (belt-and-braces;
        // they're already nonEditable in the grid). Covers both catalogue-priced
        // cells and whole procurement (non-stocked) items.
        if (kind === 'price' && (isProcPrice(item, period) || procurementItem(item))) continue;

        const existing = item.periods[period] || {
          quantity: 0, unit_price: 0, price_source: 'MANUAL', price_id: null,
        };

        if (kind === 'qty') {
          item.periods[period] = { ...existing, quantity: num };
          // Editing a quantity acknowledges any UoM change procurement made:
          // flip ALL of this item's PROC_UOM lines back to PROCUREMENT.
          if (Object.values(item.periods).some((c) => c?.price_source === 'PROC_UOM')) {
            for (const pk of Object.keys(item.periods)) {
              if (item.periods[pk]?.price_source === 'PROC_UOM') {
                item.periods[pk] = { ...item.periods[pk], price_source: 'PROCUREMENT' };
                touched[`${key}:${pk}`] = true;
              }
            }
          }
        } else if (kind === 'price') {
          // Manual price entry => source becomes MANUAL, drop any price_id link.
          item.periods[period] = { ...existing, unit_price: num, price_source: 'MANUAL', price_id: null };
        }
        touched[`${key}:${period}`] = true;
      }

      setDirty((prevDirty) => ({ ...prevDirty, ...touched }));
      return next;
    });
  };

  // ---- Save ---------------------------------------------------------------
  const handleSave = async () => {
    const changes = [];
    for (const item of items) {
      const key = stableKey(item);
      for (const p of PERIODS) {
        if (!dirty[`${key}:${p}`]) continue;
        const c = item.periods[p];
        if (!c) continue;
        // price_source:
        //   - catalogue-resolved (proc_item_prices)      -> PROCUREMENT
        //   - registered non-stocked item (bud_opex_items):
        //         price already set by procurement        -> PROCUREMENT
        //         still pending                            -> PROC_NOT_SET
        //   - otherwise                                    -> MANUAL
        const proc = isProcPrice(item, p);
        let priceSource;
        if (proc) priceSource = 'PROCUREMENT';
        else if (procurementItem(item)) {
          // Preserve the cell's procurement state (PROCUREMENT / PROC_UOM);
          // anything else on a procurement item is still pending.
          priceSource = (c.price_source === 'PROCUREMENT' || c.price_source === 'PROC_UOM')
            ? c.price_source
            : 'PROC_NOT_SET';
        } else priceSource = 'MANUAL';
        changes.push({
          item_label: item.item_label,
          stock_code: item.stock_code || null,
          financial_period: p,
          quantity: c.quantity ?? 0,
          unit_price: proc ? item.proc_price[p] : (c.unit_price ?? 0),
          price_source: priceSource,
          price_id: proc ? (c.price_id || null) : null,
          unit_of_measure: item.unit_of_measure || null,
        });
      }
    }

    if (changes.length === 0) { onClose?.(); return; }

    setIsSaving(true);
    try {
      await budgetService.saveOpexBuild(glLineId, changes);
      setDirty({});
      onSaved?.();   // parent refreshes the budget grid (GL values changed)
      onClose?.();
    } catch (err) {
      console.error('Failed to save build:', err.response?.data || err.message);
      // Keep the popup open so the user doesn't lose edits.
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = Object.keys(dirty).length > 0;

  const headerLabel = lineMeta
    ? `${lineMeta.account_code} — ${lineMeta.account_description || ''}  (${lineMeta.cost_center_code || ''})`
    : 'P×Q Build';

  const toolbar = (
    <>
      <MantleGridButton onClick={openAdd}>+ Add item</MantleGridButton>
      <MantleGridButton onClick={expandAll}>Expand all</MantleGridButton>
      <MantleGridButton onClick={collapseAll}>Collapse all</MantleGridButton>
      <MantleGridButton onClick={handleSave} accent={hasChanges}>
        {isSaving ? 'Saving…' : 'Save & close'}
      </MantleGridButton>
      <MantleGridButton onClick={onClose}>Close</MantleGridButton>
    </>
  );

  return (
    <>
      <MantleGrid
        rows={gridRows}
        columns={columns}
        isLoading={isLoading}
        onCellsChanged={handleChanges}
        onFocusLocationChanging={handleFocusLocationChanging}
        hint={`${headerLabel}  ·  click an item name to collapse/expand  ·  grey price = set by procurement  ·  ❗ = price pending  ·  ⚠️ UoM changed — edit a quantity to confirm`}
        toolbar={toolbar}
        stickyTopRows={2}
        stickyBottomRows={1}
      />

      {addOpen && (
        <AddOpexItemModal
          existingKeys={existingKeys}
          catalog={catalog}
          canRegister={canRegister}
          busy={addBusy}
          error={addError}
          onAdd={confirmAdd}
          onClose={() => { if (!addBusy) { setAddOpen(false); setAddError(''); } }}
        />
      )}
    </>
  );
};

export default OpexBuildGrid;
